import { AxiosResponse } from 'axios'

export interface Address {
  street: string
  city: string
  state: string
  zip: string
}

export interface Patient {
  tin: string
  status: string
  memberId: string
  memberUuid: string
  dob: string
  compliant: boolean
  patientName: string
  address: Address
}

export interface MemberReferral {
  referralId: string
  referredProviderNpi: string
  referredProvider: string
  referredAddress1: string
  referredAddress2: string
  referredCity: string
  referredState: string
  referredZipCode: string
  referralReason: string
  referralCreatedDate: string
}

export interface NetworkDetailsConfigs {
  configName: string
  networkCodes: string
  uuid: string
  directoryType: string
  includePreEnrollmentSite: boolean
}

export interface NetworkDetailsDetails {
  providerDomainNetwork: string,
  networkCodes: string,
  networkName: string
}


export interface Member {
  coverage: {
    benifitPlan: string
    effectiveDate: string
    planEndDate: string
    planStartDate: string
    rxBenifitPlan: string
    sbcUrl: string
    status: string
    displayStatus: string
    isVerifyNetworkStatusEnabled: boolean
  }
  demographics: {
    address: Address
    phone: string | null
  }
  dob: string
  fullDirectory: boolean
  memberId: string
  memberUuid: string
  patientName: string
  pcpIsTerminated?: boolean
  pcp: {
    providerName: string
    practiceName: string
    address: Address
    phone: string
    virtualOption: string | null
  }
  networkDetails: {
    codes: string[]
    configs: NetworkDetailsConfigs
    details: Array<NetworkDetailsDetails>
  }
  referrals: MemberReferral[]
  remain: number
  tin: string
  vists: number
}

export type AxiosError = AxiosResponse<{
  error: {
    errorCode: number
    message: string
  }
}>

export interface Specialty {
  code: string
  isCommonSpecialty: boolean
  isFacility: boolean
  isMember: boolean
  isOe: boolean
  isProvider: boolean
  level1: string
  level2: string
  specialty: string
}

export interface ReferralProvider {
  referralTool: boolean
  referralWorkflow: string
  specialtyList: Specialty[]
}

export interface ProviderDetails {
  boardCertification: string | null
  centerOfExcellence: string | null
  distance: string
  gender: string
  highValue: boolean
  isAcceptingNewPatients: boolean
  key: string
  location: Address
  isMedicalNeighborhood: boolean
  medicareSpecialtyCode: string
  medicareSubSpecialtyCode: string | null
  npi: string
  phone: string
  practiceName: string
  practitionerNpi: string
  preferredProvider: boolean
  primarySpecialty: string
  profilePicture: string | null
  providerNameTitle: string
  providerScore: string | null
  subSpecialty: string | null
  tin: string
  virtualOption: string | null
  practiceId: string
}

export interface PlanDetail {
  cmsId: string
  networkIndicators: {
    networkName: string
  }[]
  networkDetails: {
    codes: string[]
    configs: NetworkDetailsConfigs
    details: Array<NetworkDetailsDetails>
  }
  networkLink: string
  networkName: string
  pBMName: string
  planName: string
  planUuid: string
  referralDuration: number
  referralNumber: number
  rxBenefitPlan: string | null
  alert: string
  telemedicine: string | null
}

export interface GetProvidersParams {
  npi?: string
  address?: string
  specialties?: string
  distance: string
  planUuid?: string
  isPcp?: string
}

export interface SubmitReferralRequestData {
  key: string
  memberUuid: string
  npi: string
  referralReason: string
  referralSpecialty: string
  submissionDate: string
  secondaryReferredToCarepoints?: ProviderDetails[]
}

export interface GetProviderDetailParams {
  npi: string
  key: string
}

export interface ServiceLine {
  codes: string[]
  isOeDoctor: boolean
  isOeFacility: boolean
  isFilter: boolean
  isProviderDoctor: boolean
  isProviderFacility: boolean
  isPcp: boolean
  name: string
}

export interface ServiceLineOptions {
  label: string
  codes: string
  isPcp: boolean
}

export type GenderOptions = 'anyGender' | 'male' | 'female'

export type FilterOptions = {
  distance: number // miles
  gender: GenderOptions
  newPatients: boolean
  withVirtualCare: boolean
  serviceLine: string[]
}

export enum SearchType {
  PRACTICE = 'practice',
  PRACTITIONER = 'practitioner',
  FACILITY = 'facility',
  PRACTITIONER_BY_NPI = 'practitioner-by-npi',
}

export type ApiGenderOptions = 'm' | 'f'

export type VirtualOptions = 'n' | 'y' | 'only'

export type PublishStatus = 'Y' | 'N'

export type PracticeAddress = {
  address1: string
  address2: string | null
  city: string
  state: string
  street: string
  zip: string
}

interface GenericProviderObject {
  distance?: number // miles
  lat: number
  long: number
  network: string | string[]
  practiceName: string
}

export interface Practice extends GenericProviderObject {
  accessRestrictionCount?: unknown
  genderCount: Record<ApiGenderOptions, number>
  index?: number
  localUuid?: string
  nbrProviders: number
  practiceAddress: PracticeAddress
  practicePhone: string
  unfilteredProviderCount: number
  unfilteredVirtualCareOnlyCount: Partial<Record<VirtualOptions, number>> | null
  virtualCareOnlyCount?: Partial<Record<VirtualOptions, number>> | null
  practiceId?: string
}

export interface Practitioner
  extends GenericProviderObject,
    Omit<PracticeAddress, 'street' | 'zip'> {
  accessRestrictionCount?: unknown
  firstName: string
  lastName: string
  gender: ApiGenderOptions | null
  isAcceptingNewPatients: boolean
  isPcp: boolean
  minAge: string | number
  maxAge: string | number
  medicareSpecialtyCode: string
  medicareSubSpecialtyCode: string | null
  npi: string
  primarySpecialty: string // name, not code
  publish: PublishStatus
  practicePhone: string
  subSpecialty: unknown | null
  title: string
  virtualOption: VirtualOptions
  zipCode: string
  practiceId: string
}

export interface Facility
  extends GenericProviderObject,
    Omit<PracticeAddress, 'street' | 'zip'> {
  accessRestrictions: unknown | null
  address1Search: string
  address2Search: string | null
  billingAddress1: string
  billingAddress1Search: string
  billingAddress2: string | null
  billingAddress2Search: string | null
  billingCity: string
  billingCitySearch: string
  billingEmail: string | null
  billingFax: string | null
  billingPhoneNumber: string | null
  billingState: string
  billingStateSearch: string
  billingZipCode: string
  businessAs: string
  businessAsSearch: string
  citySearch: string
  county: unknown | null
  created: string
  effectiveDate: string
  endDate: unknown | null
  fax: unknown | null
  googleFormattedAddress: string
  legalName: string
  legalNameSearch: string
  modified: string
  'network#state#city#zipCode#address1#address2#specialty': string
  'network#zipCode': string
  networkGeohashKey: string
  nonpublishReason: string
  npi: string
  paymentName: string
  phone: string
  practiceNameSearch: string
  practiceNameTrunc: string
  publish: PublishStatus
  specialtyCode: string
  specialtyType: string
  specialtyTypeOriginal: string
  startDate: string
  stateSearch: string
  taxonomyCode: unknown | null
  terminationDate: unknown | null
  tin: string
  uspsFormattedAddress: string
  uuid: string
  version: string
  zipCode: string
}

export type SearchResults = Practice[] | Practitioner[] | Facility[]

export enum CATEGORY {
  NONE = '',
  DOCTOR = 'doctor',
  FACILITY = 'facility',
}

export interface SearchParams {
  zipCode: string
  category: CATEGORY
  networks: string[]
  distance?: number | string
  serviceLines?: string
  name?: string
  gender?: GenderOptions
  acceptingNewPatients?: boolean
  withVirtualCare?: boolean
  specialtyFilters?: string[]
  npi?: string
  tin?: string
}

export type GetProvidersSearch = (
  | (Pick<SearchParams, 'zipCode' | 'name' | 'serviceLines'> & {
      npi?: never
      tin?: never
    })
  | (Pick<SearchParams, 'npi'> & {
      zipCode?: never
      name?: never
      serviceLines?: never
      tin?: never
    })
  | (Pick<SearchParams, 'tin'> & {
      npi?: never
      zipCode?: never
      name?: never
      serviceLines?: never
    })
) & {
  networks: string[]
}

export type GetProvidersFilter = Pick<
  SearchParams,
  | 'distance'
  | 'gender'
  | 'acceptingNewPatients'
  | 'withVirtualCare'
  | 'specialtyFilters'
>

export interface GetPractitionersParams {
  search: Omit<GetProvidersSearch, 'zipCode' | 'networks'>
  filter?: GetProvidersFilter
  pos: {
    lat: number | string
    long: number | string
  }
  isVirtualCareOnly?: boolean
  networks?: string[]
  practice?: string
  practiceId: string
}

export interface GetDoctors {
  distance: string
  isPcp: boolean
  networks: string[]
  specialties: string
  virtualCareAvailable: boolean
  virtualCareOnly: boolean
  virtualCareUnavailable: boolean
  zipCode: string
  specialtyFilters: { name: string; code: string }[]
  count: {
    total: number
    unfilteredTotal: number
    acceptingNewPatients: number
    female: number
    genderNotInformed: number
    male: number
    virtualCare: { available: number; only: number; unavailable: number }
  }
  data: {
    providers: Practice[]
    practitioners: Practitioner[]
  }
}

export interface GetFacilities {
  distance: string
  networks: string[]
  serviceLines: string[]
  zipCode: string
  specialties: string
  data: Facility[]
  specialtyFilters: { name: string; code: string }[]
  count: {
    total: number
  }
}

export interface PractitionerSummary {
  providerFirstName: string
  providerLastName: string
  title: string
  specialty: string
  specialtyCode: string
  subSpecialty: unknown | null
  subSpecialtyCode: unknown | null
  gender: ApiGenderOptions
  npi: string
  key: string
  tin: string
  network: string
  zipCode: string
  lat: number
  long: number
  isPcp: boolean
  practiceName: string
  isAcceptingNewPatients: boolean
  virtualOption: VirtualOptions
}

export interface GetPractitioners {
  count: {
    total: number
    acceptingNewPatients: number
    female: number
    genderNotInformed: number
    male: number
    virtualCare: { available: number; only: number; unavailable: number }
  }
  data: PractitionerSummary[]
  isPcp: boolean
  latitude: string
  longitude: string
  networks: string[]
  practiceName: string
  specialties: string
  virtualCareAvailable: boolean
  virtualCareOnly: boolean
  virtualCareUnavailable: boolean
}

export interface DoctorDetails {
  endDate: unknown | null
  lastName: string
  state: string
  startDate: string
  stateSearch: string
  emrVersion: unknown | null
  networkFromFile: string
  'address1Search#address2Search#city': string
  hospitalPrivlidges: unknown | null
  googleFormattedAddress: string
  address1: string
  'network#lat#long': string
  address2: string
  primarySpecialty: string
  practicePhone: string
  citySearch: string
  'network#primarySpecialty': string
  emrType: unknown | null
  providerLocationType: unknown | null
  billingAddress1Search: string
  publish: PublishStatus
  boardName: string
  'lastName#network#zipCode': string
  isAcceptingNewPatients: boolean
  billingStateSearch: string
  isPcp: boolean
  npi: string
  practiceWebpageURL: unknown | null
  'firstName#lastName': string
  degree: string
  maxAge: number
  firstName: string
  subSpecialtySearch: unknown | null
  billingEmail: unknown | null
  suffix: unknown | null
  isGeriatric: boolean
  billingState: string
  networkGeohashKey: string
  modified: string
  version: string
  terminationDate: unknown | null
  geohashKey: number
  panelOpen: VirtualOptions
  isSurgeon: boolean
  lastNameSearch: string
  medicareSubSpecialtyCode: unknown | null
  gender: ApiGenderOptions
  minAge: number
  'lat#long': string
  billingAddress2Search: unknown | null
  uuid: string
  boardCertified: PublishStatus
  subSpecialty: unknown | null
  taxonomyCode: unknown | null
  taxId: string
  billingZipCode: string
  countySearch: unknown | null
  'network#zipCode': string
  uspsFormattedAddress: string
  geohash: number
  address1Search: string
  zipCode: string
  middleName: string
  practiceName: string
  practiceNameSearch: string
  county: unknown | null
  middleNameSearch: string
  medicareSpecialtyCode: string
  long: number
  title: string
  primarySpecialtySearch: string
  address2Search: string
  billingCitySearch: string
  panelSlots: unknown | null
  billingPhoneNumber: string
  practiceNameTrunc: string
  paymentName: string
  network: string
  billingCity: string
  isPediatrician: false
  city: string
  virtualOption: VirtualOptions
  practiceFax: string
  practiceEmail: unknown | null
  firstNameSearch: string
  billingAddress2: unknown | null
  created: string
  'network#state#city#zipCode#address1#address2#practiceName': string
  billingAddress1: string
  languages: string
  minPatientAge: number
  tin: string
  lat: number
  billingFax: string
  maxPatientAge: number
  effectiveDate: string
  educations:
    | { name: string; type?: string | null; year?: number }[]
    | string[]
    | string
    | null
  distance: string | null
  hospitalAffiliations?: string
}

export interface Coords {
  lat: number
  lon: number
}

export interface OpensearchPractice {
  _id: string
  geohashKey: number
  zipCode: string
  city: string
  address1: string
  address2?: string
  practicePhone: string
  created: string
  virtualOption: string
  displayPriority: number
  network: string
  practiceName: string
  geohash: string
  practiceNameSort: string
  modified: string
  state: string
  distance: string
  providersCount: number
  isAcceptingNewPatients: boolean
  location: {
    lat: number
    lon: number
  }
  lat: number
  long: number
}

export interface OpensearchProvider {
  _id: string
  npi: string
  sortKey: string
  uuid: string
  isAcceptingNewPatients: boolean
  isGeriatric: boolean
  isPcp: boolean
  isPediatrician: boolean
  isSurgeon: boolean
  boardCertified: boolean
  hospitalistFlag: boolean
  isMedicalNeighborhood: boolean
  panelOpen: boolean
  practiceId: string
  codedDegree: string
  degree: string
  title: string
  firstName: string
  lastName: string
  fullName: string
  fullNameSort: string
  location: {
    lat: number
    lon: number
  }
  minAge: number
  maxAge: number
  minPatientAge: number
  maxPatientAge: number
  virtualOption: string
  medicareSpecialtyCode: string
  gender: string
  publish: boolean
  displayPriority: number
  modified: string
  created: string
  geohash: string
  state: string
  startDate: string
  networkFromFile: string
  address1: string
  taxonomyCodeDescription: string
  primarySpecialty: string
  practicePhone: string
  providerLocationType: string
  practiceWebpageURL: string
  suffix: string
  billingState: string
  networkGeohashKey: string
  version: string
  geohashKey: number
  taxonomyCode: string
  taxId: string
  billingZipCode: string
  zipCode: string
  jvNetworkID: string
  practiceName: string
  county: string
  nonpublishReason: string
  panelSlots: string
  rosterID: string
  practiceNameTrunc: string
  paymentName: string
  network: string
  billingCity: string
  city: string
  practiceFax: string
  billingAddress1: string
  networkParentCode: string
  languages: string
  tin: string
  effectiveDate: string
  lat: number
  long: number
  distance: string
  key: string
  hospitalAffiliations: string
  educations: string
  boardName: string
  address2: string
}

export interface OpensearchProviderResponse {
  meta: Record<string, any>
  count: {
    total: number
  }
  specialtyFilters: { name: string; code: string }[]
  data: OpensearchProvider[]
}

export interface OpensearchPracticeResponse {
  meta: Record<string, any>
  count: {
    totalPractices: number
    totalProviders: number
  }
  specialtyFilters: { name: string; code: string }[]
  data: OpensearchPractice[]
}
